import { DotLottie } from '@lottiefiles/dotlottie-web';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Initialize Lottie animations
 * Looks for elements with data-lottie-src attribute and initializes them
 * All animations only start when they enter the viewport
 * Supports scroll-based scrubbing with data-lottie-scroll="true"
 */
document.addEventListener('DOMContentLoaded', () => {
	const lottieContainers = document.querySelectorAll('[data-lottie-src]');

	lottieContainers.forEach((container) => {
		const src = container.getAttribute('data-lottie-src');
		const loop = container.getAttribute('data-lottie-loop') !== 'false';
		const autoplay = container.getAttribute('data-lottie-autoplay') !== 'false';
		const speed = parseFloat(container.getAttribute('data-lottie-speed')) || 1;
		const scrollControl = container.getAttribute('data-lottie-scroll') === 'true';
		const scrollStart = container.getAttribute('data-lottie-scroll-start') || 'top bottom';
		const scrollEnd = container.getAttribute('data-lottie-scroll-end') || 'bottom top';
		const autoplayStart = container.getAttribute('data-lottie-autoplay-start') || 'center center';

		if (!src) {
			console.warn('Lottie container missing data-lottie-src attribute', container);
			return;
		}

		try {
			// Always start with autoplay disabled - we'll control it with ScrollTrigger
			const dotLottie = new DotLottie({
				canvas: container,
				src: src,
				loop: scrollControl ? false : loop,
				autoplay: false, // Always false - controlled by viewport visibility
				speed: speed,
			});

			// Store instance on element for potential external control
			container.lottieInstance = dotLottie;

			// If scroll control is enabled, set up ScrollTrigger for scrubbing
			if (scrollControl) {
				// Wait for the animation to load before setting up scroll control
				dotLottie.addEventListener('load', () => {
					const totalFrames = dotLottie.totalFrames;

					const scrollTrigger = ScrollTrigger.create({
						trigger: container,
						start: scrollStart,
						end: scrollEnd,
						scrub: 1,
						onUpdate: (self) => {
							// Calculate which frame to show based on scroll progress
							const targetFrame = Math.floor(self.progress * (totalFrames - 1));

							// Seek to the specific frame
							dotLottie.setFrame(targetFrame);
						}
					});

					// Manually check initial position after ScrollTrigger is created
					scrollTrigger.refresh();

					// Set initial frame based on current scroll position
					const progress = scrollTrigger.progress || 0;
					if (progress > 0) {
						const targetFrame = Math.floor(progress * (totalFrames - 1));
						dotLottie.setFrame(targetFrame);
					}
				});
			} else if (autoplay) {
				// For autoplay animations, play only when in viewport
				let hasPlayed = false;

				// Wait for animation to load before setting up viewport detection
				dotLottie.addEventListener('load', () => {
					const scrollTrigger = ScrollTrigger.create({
						trigger: container,
						start: autoplayStart,
						onEnter: () => {
							if (!hasPlayed || loop) {
								dotLottie.play();
								if (!loop) {
									hasPlayed = true;
								}
							}
						},
						onEnterBack: () => {
							// Play again when scrolling back if loop is enabled
							if (loop) {
								dotLottie.play();
							}
						}
					});

					// Manually check if element is in view on load
					scrollTrigger.refresh();

					// Play if already in viewport
					if (scrollTrigger.isActive && (!hasPlayed || loop)) {
						dotLottie.play();
						if (!loop) {
							hasPlayed = true;
						}
					}
				});
			}
		} catch (error) {
			console.error('Error initializing Lottie animation:', error);
		}
	});
});

export default function initLottie() {
	// Initialization happens in DOMContentLoaded above
	// This export allows for future enhancements if needed
}
